import React from "react";

export function Chip({ tone = "stage", children }) {
  const tones = {
    stage: "bg-[#E9E4F5] text-[#5B3FA0]",
    "risk-low": "bg-[#E4F4EB] text-[#2A7A54]",
    "risk-med": "bg-[#FBF0DF] text-[#B7660C]",
    "risk-high": "bg-[#FBEAE8] text-[#B93B32]",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${
        tones[tone] || tones.stage
      }`}
    >
      {children}
    </span>
  );
}

export function RiskChip({ risk }) {
  const tone =
    risk === "Low" ? "risk-low" : risk === "High" ? "risk-high" : "risk-med";
  return <Chip tone={tone}>{risk}</Chip>;
}

export function OnboardProgress({ done, total }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const label = done === 0 ? "Not started" : `${done} / ${total} steps`;
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-[70px] overflow-hidden rounded bg-[#D3E0E8]">
        <div
          className="h-full rounded bg-[#12838C]"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="whitespace-nowrap text-[11px] text-gray-500">{label}</span>
    </div>
  );
}

export function StatusBanner({ items }) {
  return (
    <div className="mb-3.5 flex flex-wrap items-center gap-5 rounded-lg bg-[#E8EFF3] px-4 py-3.5 dark:bg-acs-primary-navy/20">
      {items.map((item) => (
        <div key={item.label} className="flex flex-col gap-0.5">
          <span className="text-[10px] font-bold uppercase tracking-wide text-[#0F3D5C]/70 dark:text-acs-dark-text/70">
            {item.label}
          </span>
          {item.progress != null ? (
            <div className="h-1.5 w-[130px] overflow-hidden rounded bg-[#D3E0E8]">
              <div
                className="h-full bg-[#12838C]"
                style={{ width: `${item.progress}%` }}
              />
            </div>
          ) : (
            <span
              className={`text-[13px] font-bold ${
                item.valueClass || "text-[#0F3D5C] dark:text-acs-dark-text"
              }`}
            >
              {item.value}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

export function NoteFlag({ children }) {
  return (
    <div className="mb-3.5 rounded-md bg-[#E4F3F3] px-3 py-2 text-[11.5px] text-[#12838C]">
      {children}
    </div>
  );
}

export function DocRow({ name, statusLabel, received }) {
  return (
    <div className="mb-2 flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3.5 py-3 dark:border-acs-dark-border dark:bg-acs-dark-surface">
      <span className="text-[12.5px] font-semibold text-gray-900 dark:text-acs-dark-text">
        {name}
      </span>
      <span
        className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
          received
            ? "bg-[#E4F4EB] text-[#2A7A54]"
            : "bg-[#FBF0DF] text-[#B7660C]"
        }`}
      >
        {statusLabel}
      </span>
    </div>
  );
}

export function RateOnboardChip({ status, label }) {
  const cls =
    status === "active" || status === "extracted"
      ? "bg-[#E4F4EB] text-[#2A7A54]"
      : status === "failed"
        ? "bg-[#FCE8E6] text-[#B93B32]"
        : status === "extracting"
          ? "bg-[#E8EFF3] text-[#223688]"
          : status === "superseded"
            ? "bg-gray-100 text-gray-500"
            : "bg-[#FBF0DF] text-[#B7660C]";
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold ${cls}`}>
      {label}
    </span>
  );
}

export function MarketTag({ tag, label }) {
  const cls =
    tag === "good"
      ? "text-[#2A7A54]"
      : tag === "bad"
        ? "text-[#B93B32]"
        : "text-[#B7660C]";
  return <span className={`text-[11px] font-semibold ${cls}`}>{label}</span>;
}

export function FieldRow({ label, value, valueClass = "" }) {
  return (
    <div className="flex justify-between border-b border-gray-200 py-2.5 text-[12.5px] last:border-0 dark:border-acs-dark-border">
      <span className="text-gray-500">{label}</span>
      <span
        className={`font-mono text-xs font-semibold text-gray-900 dark:text-acs-dark-text ${valueClass}`}
      >
        {value}
      </span>
    </div>
  );
}

export function SectionTitle({ children }) {
  return (
    <h3 className="mb-2.5 text-[12px] font-bold uppercase tracking-wide text-gray-600 dark:text-acs-dark-text/70">
      {children}
    </h3>
  );
}

export function CardShell({ children, className = "" }) {
  return (
    <div
      className={`rounded-[10px] border border-gray-200 bg-white dark:border-acs-dark-border dark:bg-acs-dark-surface ${className}`}
    >
      {children}
    </div>
  );
}

export function PageHead({ title, subtitle, actions }) {
  return (
    <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
      <div>
        <h1 className="m-0 text-xl font-bold text-gray-900 dark:text-acs-dark-text">
          {title}
        </h1>
        {subtitle ? (
          <div className="mt-1 text-[12.5px] text-gray-500">{subtitle}</div>
        ) : null}
      </div>
      {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
    </div>
  );
}

export function CsButton({
  children,
  onClick,
  variant = "primary",
  type = "button",
  className = "",
  disabled = false,
}) {
  const variants = {
    primary:
      "bg-[#0F3D5C] text-white hover:bg-[#0C3049] dark:bg-acs-primary-navy",
    accent: "bg-[#12838C] text-white hover:bg-[#0f6f76]",
    ghost:
      "border border-gray-200 bg-transparent text-[#0F3D5C] hover:bg-gray-50 dark:border-acs-dark-border dark:text-acs-dark-text dark:hover:bg-acs-dark-hover",
  };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`rounded-md px-4 py-2 text-[13px] font-semibold transition-none disabled:cursor-not-allowed disabled:opacity-50 ${
        variants[variant] || variants.primary
      } ${className}`}
    >
      {children}
    </button>
  );
}

export function ActivityIcon({ type }) {
  const map = { call: "📞", email: "✉️", meeting: "🤝" };
  return (
    <div className="flex h-[26px] w-[26px] flex-shrink-0 items-center justify-center rounded-md bg-gray-100 text-xs dark:bg-acs-dark-hover">
      {map[type] || "•"}
    </div>
  );
}

export function TimelineItem({ icon, who, txt, when }) {
  return (
    <div className="flex gap-2.5 border-b border-gray-200 py-2.5 last:border-0 dark:border-acs-dark-border">
      <ActivityIcon type={icon} />
      <div>
        <div className="text-[12.5px] font-semibold text-gray-900 dark:text-acs-dark-text">
          {who}
        </div>
        <div className="mt-0.5 text-xs text-gray-600 dark:text-acs-dark-text/80">
          {txt}
        </div>
        <div className="mt-1 font-mono text-[10.5px] text-gray-400">{when}</div>
      </div>
    </div>
  );
}

export function FormField({ label, hint, children, full = false }) {
  return (
    <div className={`mb-4 ${full ? "col-span-full" : ""}`}>
      {label ? (
        <label className="mb-1.5 block text-xs font-semibold text-gray-600 dark:text-acs-dark-text/70">
          {label}
        </label>
      ) : null}
      {children}
      {hint ? <div className="mt-1 text-[11px] text-gray-400">{hint}</div> : null}
    </div>
  );
}

export const inputClass =
  "w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-[13px] text-gray-900 outline-none focus:border-[#12838C] dark:border-acs-dark-border dark:bg-acs-dark-surface dark:text-acs-dark-text";
